import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import ThemedChartTooltip, { ThemedTooltipBox } from "../../shared/ThemedChartTooltip";
import InfoTip from "../../shared/InfoTip";
import { BRAND_BLUE } from "../../shared/colors";

/**
 * BenchmarkBars — network_benchmark evidence component.
 *
 * Paired bars per metric: yours against the network median.
 * Caption with peer count. No other property is ever named.
 * Below 4 peers the comparison is withheld entirely.
 *
 * Spec references: §7 (network_benchmark).
 */

const fmtNum = (v) => {
  if (v == null) return "—";
  const n = Number(v);
  return Number.isInteger(n) ? n.toLocaleString() : n.toFixed(1);
};

/** Convert a metric key like "rain_sensitivity" → "Rain Sensitivity" for the Y-axis. */
const shortLabel = (key) => {
  if (!key) return "";
  return key
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

/** Format the vs-network delta as a signed percentage point string. */
const fmtDelta = (v) => {
  if (v == null) return "—";
  const n = Number(v);
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}`;
};

const BenchmarkBars = ({ panel }) => {
  const f = panel.facts || {};
  const metrics = f.metrics || [];
  const peers = f.peers || 0;
  const comparable = f.comparable !== false;

  if (metrics.length === 0) return null;

  const hasComparisons =
    comparable &&
    metrics.some((m) => m.network_median != null && m.vs_network != null);

  const chartData = metrics.map((m) => ({
    shortLabel: shortLabel(m.metric),
    fullLabel: m.label,
    You: Number(m.yours) || 0,
    Median: m.network_median != null ? Number(m.network_median) : null,
  }));

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
            <span>Your Sensitivity vs. Network Median</span>
            <InfoTip text="Paired bars comparing your weather sensitivity to the network median across key metrics. No individual properties are identified. A higher sensitivity is not a failing — it means bookings react more strongly to conditions." />
          </h3>
        </div>

        <ResponsiveContainer
          width="100%"
          height={Math.max(180, metrics.length * 56)}
        >
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
            barGap={2}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#374151"
              horizontal={false}
            />
            <XAxis
              type="number"
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              axisLine={{ stroke: "#374151" }}
              tickLine={false}
              allowDecimals={false}
            />
            <YAxis
              dataKey="shortLabel"
              type="category"
              tick={{ fontSize: 11, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
              width={130}
            />
            <ThemedChartTooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const datum = payload[0]?.payload;
                if (!datum) return null;
                const youVal = payload.find((p) => p.dataKey === "You");
                const medVal = payload.find((p) => p.dataKey === "Median");
                return (
                  <ThemedTooltipBox title={datum.fullLabel}>
                    <div>You: {fmtNum(youVal?.value)}</div>
                    {hasComparisons && medVal?.value != null && (
                      <div>Median: {fmtNum(medVal.value)}</div>
                    )}
                  </ThemedTooltipBox>
                );
              }}
            />
            <Bar
              dataKey="You"
              fill={BRAND_BLUE}
              radius={[0, 4, 4, 0]}
              barSize={14}
            />
            {hasComparisons && (
              <Bar
                dataKey="Median"
                fill="#9CA3AF"
                radius={[0, 4, 4, 0]}
                barSize={14}
              />
            )}
          </BarChart>
        </ResponsiveContainer>

        {/* Legend — always below the graph, centred */}
        {hasComparisons && (
          <div className="flex items-center justify-center gap-4 text-[11px] text-gray-500 dark:text-gray-400 mt-3">
            <span className="flex items-center gap-1.5">
              <span
                className="inline-block w-3 h-3 rounded-sm"
                style={{ backgroundColor: BRAND_BLUE }}
              />
              You
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm bg-gray-400" />
              Network median
            </span>
          </div>
        )}
      </div>

      {/* Per-metric breakdown table with full labels */}
      <div className="border-t border-gray-100 dark:border-gray-800 pt-3">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-gray-400 dark:text-gray-500 uppercase tracking-wider text-[10px]">
              <th className="text-left py-1.5 pr-3 font-semibold">Metric</th>
              <th className="text-right py-1.5 px-3 font-semibold">You</th>
              <th className="text-right py-1.5 px-3 font-semibold">Median</th>
              <th className="text-right py-1.5 pl-3 font-semibold">Diff</th>
            </tr>
          </thead>
          <tbody>
            {metrics.map((m) => {
              const delta = m.vs_network;
              const hasMedian = m.network_median != null && m.vs_network != null;
              // A higher sensitivity is not a failing — colour the delta neutrally.
              const deltaColor =
                !hasMedian
                  ? "text-gray-400 dark:text-gray-500"
                  : delta > 0
                    ? "text-blue-600 dark:text-blue-400"
                    : delta < 0
                      ? "text-gray-500 dark:text-gray-400"
                      : "text-gray-400 dark:text-gray-500";

              return (
                <tr
                  key={m.metric}
                  className="border-t border-gray-100 dark:border-gray-800"
                >
                  <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">
                    {m.label}
                  </td>
                  <td className="py-2 px-3 text-right font-semibold text-gray-700 dark:text-gray-300">
                    {fmtNum(m.yours)}
                  </td>
                  <td className="py-2 px-3 text-right text-gray-500 dark:text-gray-400">
                    {hasMedian ? fmtNum(m.network_median) : "—"}
                  </td>
                  <td className={`py-2 pl-3 text-right font-medium ${deltaColor}`}>
                    {hasMedian ? `${fmtDelta(delta)} pts` : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BenchmarkBars;
